// «Счёт агента» (разбор 26.09): что дали решения агента за 30 дней — сколько решений, какие сработали по итогам,
// сколько сберегли или заработали (оценка платформы там, где её можно посчитать), насколько сбылись его прогнозы
// и что сберегли бы невыполненные советы. Самостоятельный блок вверху «Журнала решений»: данные —
// /api/agent/scorecard.
import { Button, Card, pct, Skeleton, signedUsd, usd, when } from '@autoaff/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';

interface ScoreItem {
  id: number;
  title: string;
  type: string;
  kind: 'executed' | 'not_executed' | 'decision';
  verdict: string | null;
  effectUsd: number | null;
  createdAt: string;
}

interface Scorecard {
  days: number;
  decisions: number;
  proposals: number;
  reviewed: { worked: number; failed: number; unclear: number };
  awaiting: number;
  money: { usd: number; count: number };
  missed: { usd: number; count: number };
  accuracy: { met: number; total: number };
  best: ScoreItem[];
  worst: ScoreItem[];
}

const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

function Tile({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: string }) {
  return (
    <div className="kpi">
      <span>{label}</span>
      <b className={tone}>{value}</b>
      <small>{sub}</small>
    </div>
  );
}

function Items({ title, items }: { title: string; items: ScoreItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="min-w-0">
      <h3 style={{ margin: '0 0 6px', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{title}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
        {items.map((i) => (
          <li
            key={i.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 12,
              fontSize: 'var(--fs-sm)',
            }}
          >
            <span className="min-w-0" style={{ overflowWrap: 'anywhere' }}>
              {i.title}
              <small className="muted" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
                {when(i.createdAt)}
              </small>
            </span>
            <b className={`tnum ${(i.effectUsd ?? 0) >= 0 ? 'pos' : 'neg'}`}>{signedUsd(i.effectUsd ?? 0)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AgentScore() {
  const q = useQuery({
    queryKey: ['agent', 'scorecard'],
    queryFn: () => api<Scorecard>('/api/agent/scorecard?days=30'),
    refetchInterval: 5 * 60_000,
  });
  if (q.isPending)
    return (
      <section className="kpis" aria-label="Счёт агента">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="kpi">
            <Skeleton w="50%" />
            <Skeleton w="60%" h={26} />
            <Skeleton w="80%" h={12} />
          </div>
        ))}
      </section>
    );
  if (q.isError)
    return (
      <Card title="Счёт агента">
        <p className="muted" style={{ margin: '0 0 8px' }}>
          Не удалось посчитать: {(q.error as Error).message}
        </p>
        <Button size="sm" onClick={() => q.refetch()}>
          Повторить
        </Button>
      </Card>
    );
  const s = q.data;
  if (s.decisions === 0) return null;
  const checked = s.reviewed.worked + s.reviewed.failed + s.reviewed.unclear;
  const moneyTone = s.money.usd > 0 ? 'pos' : s.money.usd < 0 ? 'neg' : undefined;
  return (
    <Card title={`Счёт агента за ${s.days} дней`}>
      <section className="kpis" aria-label="Счёт агента" style={{ boxShadow: 'none' }}>
        <Tile
          label="Решений"
          value={String(s.decisions)}
          sub={`заявок и советов ${s.proposals}${s.awaiting ? ` · ждут проверки ${s.awaiting}` : ''}`}
        />
        <Tile
          label="Сработало по итогам"
          value={checked ? `${s.reviewed.worked} из ${checked}` : '—'}
          sub={
            checked
              ? `не сработало ${s.reviewed.failed}, неясно ${s.reviewed.unclear}`
              : 'итогов ещё нет — агент проверит их в срок'
          }
        />
        <Tile
          label="Деньги от решений"
          value={s.money.count ? signedUsd(s.money.usd) : '—'}
          tone={moneyTone}
          sub={
            s.money.count
              ? `сбережено и заработано, оценка по ${s.money.count} ${plural(s.money.count, 'решению', 'решениям', 'решениям')} в рекламе`
              : 'посчитается после проверки выполненных решений в рекламе'
          }
        />
        <Tile
          label="Точность прогнозов"
          value={s.accuracy.total ? pct((s.accuracy.met / s.accuracy.total) * 100, 0) : '—'}
          sub={
            s.accuracy.total
              ? `сбылось ${s.accuracy.met} из ${s.accuracy.total}`
              : 'агент ещё не давал прогнозов, которые можно сверить'
          }
        />
      </section>
      {s.missed.count > 0 && (
        <p className="muted" style={{ margin: '12px 0 0', fontSize: 'var(--fs-sm)' }}>
          {s.missed.usd > 0
            ? `Советы остановить, которые не выполнили (${s.missed.count}), сберегли бы примерно ${usd(s.missed.usd)}.`
            : `Советы остановить, которые не выполнили (${s.missed.count}), оказались лишними: объекты после них не теряли денег.`}
        </p>
      )}
      {(s.best.length > 0 || s.worst.length > 0) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 'var(--gap)',
            marginTop: 16,
          }}
        >
          <Items title="Лучшие решения" items={s.best} />
          <Items title="Худшие решения" items={s.worst} />
        </div>
      )}
      <p className="muted" style={{ margin: '12px 0 0', fontSize: 'var(--fs-xs)' }}>
        Деньги — оценка платформы: разница прогнозной прибыли объекта до решения и после, в окнах одинаковой
        длины.
      </p>
    </Card>
  );
}
